import { createFileRoute } from '@tanstack/react-router';
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Table,
  Tag,
  Badge,
  Skeleton,
  Flex,
  Empty,
  Progress,
  theme
} from 'antd';
import {
  TeamOutlined,
  DashboardOutlined,
  AppstoreOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  UserOutlined,
  ThunderboltOutlined,
  SafetyCertificateOutlined,
  RobotOutlined,
  EyeOutlined,
  BankOutlined,
  CrownOutlined
} from '@ant-design/icons';
import { useNavigate } from '@tanstack/react-router';
import { IconCardTitle } from '@/components/ui/IconCardTitle';
import { DashboardPage, type DashboardStatCardConfig } from '@/components/dashboard/DashboardPage';
import { QuickActionCard } from '@/components/dashboard/QuickActionCard';
import { TenantAvatar } from '@/components/admin/TenantAvatar';
import { useTranslation } from 'react-i18next';
import { DEFAULT_CLIENTS_LIST_ROUTE_SEARCH } from '@/api/auth/defaults';
import { getRoleColor } from '@/constants/roles';
import { useAdminDashboardStats } from '@/hooks/dashboard/useAdminDashboardStats';
import { useAdminClientColumns } from '@/hooks/dashboard/useAdminClientColumns';
import { useAdminUserColumns } from '@/hooks/dashboard/useAdminUserColumns';

const { Text } = Typography;

/** Badge status per system-health state. */
const HEALTH_BADGE_STATUS: Record<string, 'success' | 'warning' | 'error'> = {
  healthy: 'success',
  warning: 'warning',
  error: 'error'
};

export const Route = createFileRoute('/admin/')({
  component: AdminDashboard
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function AdminDashboard() {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const { token } = theme.useToken();

  const {
    profile,
    profileLoading,
    clientsLoading,
    clientsError,
    usersLoading,
    usersError,
    modulesLoading,
    availableModules,
    totalClients,
    recentClients,
    totalUsers,
    recentUsers,
    totalCompanies,
    roleDistribution,
    maxRoleCount,
    topClientsByCompanies,
    totalModules,
    systemServices,
    overallHealth,
    refetchAll
  } = useAdminDashboardStats();
  const clientColumns = useAdminClientColumns();
  const userColumns = useAdminUserColumns();

  // =========================================================================
  // Stat cards
  // =========================================================================

  const statCards: DashboardStatCardConfig[] = [
    {
      key: 'clients',
      label: t('dashboard.stats.clients'),
      value: totalClients,
      icon: <TeamOutlined />,
      color: token.colorPrimary,
      loading: clientsLoading,
      onClick: () => navigate({ to: '/admin/tenants', search: DEFAULT_CLIENTS_LIST_ROUTE_SEARCH })
    },
    {
      key: 'companies',
      label: t('dashboard.stats.companies'),
      value: totalCompanies,
      icon: <BankOutlined />,
      color: token.purple,
      loading: clientsLoading,
      onClick: () => navigate({ to: '/admin/tenants', search: DEFAULT_CLIENTS_LIST_ROUTE_SEARCH })
    },
    {
      key: 'users',
      label: t('dashboard.stats.users'),
      value: totalUsers,
      icon: <UserOutlined />,
      color: token.colorSuccess,
      loading: usersLoading,
      onClick: () => navigate({ to: '/admin/users' })
    },
    {
      key: 'modules',
      label: t('dashboard.stats.modules'),
      value: totalModules,
      icon: <AppstoreOutlined />,
      color: token.orange,
      loading: modulesLoading
    }
  ];

  // =========================================================================
  // Quick actions
  // =========================================================================

  const quickActions = [
    {
      title: t('dashboard.quickActions.manageClients'),
      description: t('dashboard.quickActions.manageClientsDesc'),
      icon: <TeamOutlined />,
      color: token.colorPrimary,
      path: '/admin/tenants' as const,
      search: DEFAULT_CLIENTS_LIST_ROUTE_SEARCH
    },
    {
      title: t('dashboard.quickActions.userManagement'),
      description: t('dashboard.quickActions.userManagementDesc'),
      icon: <UserOutlined />,
      color: token.colorSuccess,
      path: '/admin/users' as const
    },
    {
      title: t('dashboard.quickActions.aiSettings'),
      description: t('dashboard.quickActions.aiSettingsDesc'),
      icon: <RobotOutlined />,
      color: token.cyan,
      path: '/admin/ai/general' as const
    },
    {
      title: t('dashboard.quickActions.aiLogs'),
      description: t('dashboard.quickActions.aiLogsDesc'),
      icon: <EyeOutlined />,
      color: token.magenta,
      path: '/admin/ai/logs' as const
    }
  ];

  // =========================================================================
  // Render
  // =========================================================================

  return (
    <DashboardPage
      icon={<DashboardOutlined />}
      title={t('dashboard.title')}
      description={
        profileLoading
          ? t('dashboard.loading')
          : profile
            ? t('dashboard.welcomeBack', { name: profile.name })
            : t('dashboard.platformOverview')
      }
      refreshLabel={t('dashboard.refresh')}
      onRefresh={refetchAll}
      errorTitle={t('dashboard.servicesUnreachable')}
      errorMessages={[
        clientsError && t('dashboard.failedClients'),
        usersError && t('dashboard.failedUsers')
      ]}
      retryLabel={t('dashboard.retry')}
      statCards={statCards}
    >
      {/* Quick Actions + System Health */}
      <Row gutter={[token.marginMD, token.marginMD]} style={{ marginBottom: token.marginLG }}>
        <Col xs={24} lg={14}>
          <Card
            title={<IconCardTitle icon={<ThunderboltOutlined />} title={t('dashboard.quickActions.title')} />}
            style={{ height: '100%' }}
          >
            <Row gutter={[token.marginSM, token.marginSM]}>
              {quickActions.map((action) => (
                <Col xs={24} sm={12} key={action.title}>
                  <QuickActionCard
                    icon={action.icon}
                    title={action.title}
                    description={action.description}
                    color={action.color}
                    onClick={() =>
                      navigate({
                        to: action.path,
                        ...('search' in action && action.search ? { search: action.search } : {})
                      })
                    }
                  />
                </Col>
              ))}
            </Row>
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          <Card
            title={
              <IconCardTitle
                icon={<SafetyCertificateOutlined />}
                color={overallHealth ? token.colorSuccess : token.colorError}
                title={t('dashboard.systemHealth.title')}
              />
            }
            extra={
              <Tag
                color={overallHealth ? 'success' : 'error'}
                icon={overallHealth ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
                style={{ marginInlineEnd: 0 }}
              >
                {overallHealth ? t('dashboard.systemHealth.allOperational') : t('dashboard.systemHealth.issuesDetected')}
              </Tag>
            }
            style={{ height: '100%' }}
          >
            <Flex vertical>
              {systemServices.map((service) => (
                <Flex
                  key={service.name}
                  justify="space-between"
                  align="center"
                  gap={token.marginSM}
                  style={{ paddingBlock: token.paddingSM }}
                >
                  <Flex align="center" gap={token.marginSM} style={{ minWidth: 0 }}>
                    <Badge status={HEALTH_BADGE_STATUS[service.status] ?? 'default'} />
                    <Text strong ellipsis>{service.name}</Text>
                  </Flex>
                  <Text
                    type={service.status === 'error' ? 'danger' : 'secondary'}
                    style={{ fontSize: token.fontSizeSM, textAlign: 'end' }}
                  >
                    {service.detail}
                  </Text>
                </Flex>
              ))}
            </Flex>
          </Card>
        </Col>
      </Row>

      {/* Recent Clients + User Role Distribution */}
      <Row gutter={[token.marginMD, token.marginMD]} style={{ marginBottom: token.marginLG }}>
        <Col xs={24} lg={14}>
          <Card
            title={<IconCardTitle icon={<TeamOutlined />} title={t('dashboard.recentClients')} />}
            extra={
              <Button
                type="link"
                size="small"
                icon={<ArrowRightOutlined />}
                iconPlacement="end"
                onClick={() =>
                  navigate({ to: '/admin/tenants', search: DEFAULT_CLIENTS_LIST_ROUTE_SEARCH })
                }
              >
                {t('dashboard.viewAll', { count: totalClients })}
              </Button>
            }
            style={{ height: '100%' }}
            styles={{ body: { padding: 0 } }}
          >
            <Table
              columns={clientColumns}
              dataSource={recentClients}
              rowKey="id"
              loading={clientsLoading}
              pagination={false}
              size="small"
              scroll={{ x: 'max-content' }}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={clientsError ? t('dashboard.failedLoadClients') : t('dashboard.noClientsYet')}
                  />
                )
              }}
              onRow={(record) => ({
                style: { cursor: 'pointer' },
                onClick: () => navigate({ to: `/admin/tenants/${record.id}` as string })
              })}
            />
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          <Card
            title={
              <IconCardTitle icon={<CrownOutlined />} color={token.purple} title={t('dashboard.userRoleDistribution')} />
            }
            style={{ height: '100%' }}
          >
            {usersLoading ? (
              <Skeleton active paragraph={{ rows: 4 }} />
            ) : roleDistribution.length > 0 ? (
              <Flex vertical>
                {roleDistribution.map((item) => (
                  <Flex
                    key={item.role}
                    justify="space-between"
                    align="center"
                    gap={token.marginSM}
                    style={{ paddingBlock: token.paddingXS }}
                  >
                    <Tag color={getRoleColor(item.role)} style={{ marginInlineEnd: 0 }}>
                      {item.role}
                    </Tag>
                    <Flex align="center" gap={token.marginXS} style={{ flex: '0 1 160px' }}>
                      <Progress
                        percent={(item.count / maxRoleCount) * 100}
                        showInfo={false}
                        status="normal"
                        size="small"
                        style={{ margin: 0, flex: 1 }}
                      />
                      <Text strong style={{ minWidth: token.controlHeight, textAlign: 'end' }}>
                        {item.count}
                      </Text>
                    </Flex>
                  </Flex>
                ))}
              </Flex>
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('dashboard.noUserData')} />
            )}
          </Card>
        </Col>
      </Row>

      {/* Recent Users + Top Clients by Companies */}
      <Row gutter={[token.marginMD, token.marginMD]}>
        <Col xs={24} lg={14}>
          <Card
            title={<IconCardTitle icon={<UserOutlined />} color={token.colorSuccess} title={t('dashboard.recentUsers')} />}
            extra={
              <Button
                type="link"
                size="small"
                icon={<ArrowRightOutlined />}
                iconPlacement="end"
                onClick={() => navigate({ to: '/admin/users' })}
              >
                {t('dashboard.viewAll', { count: totalUsers })}
              </Button>
            }
            style={{ height: '100%' }}
            styles={{ body: { padding: 0 } }}
          >
            <Table
              columns={userColumns}
              dataSource={recentUsers}
              rowKey="id"
              loading={usersLoading}
              pagination={false}
              size="small"
              scroll={{ x: 'max-content' }}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={usersError ? t('dashboard.failedLoadUsers') : t('dashboard.noUsersYet')}
                  />
                )
              }}
            />
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          <Card
            title={<IconCardTitle icon={<BankOutlined />} color={token.purple} title={t('dashboard.topClientsByCompanies')} />}
            style={{ height: '100%' }}
          >
            {clientsLoading ? (
              <Skeleton active paragraph={{ rows: 4 }} />
            ) : topClientsByCompanies.length > 0 ? (
              <Flex vertical>
                {topClientsByCompanies.map((client) => (
                  <Flex
                    key={client.id}
                    justify="space-between"
                    align="center"
                    gap={token.marginSM}
                    style={{ paddingBlock: token.paddingXS, cursor: 'pointer' }}
                    onClick={() => navigate({ to: `/admin/tenants/${client.id}` as string })}
                  >
                    <Flex align="center" gap={token.marginSM} style={{ minWidth: 0 }}>
                      <TenantAvatar name={client.name} size={token.controlHeightSM + 4} />
                      <Text strong ellipsis={{ tooltip: true }}>{client.name}</Text>
                    </Flex>
                    <Tag color="blue" style={{ marginInlineEnd: 0, flexShrink: 0 }}>
                      {client.companies?.length ?? 0}{' '}
                      {(client.companies?.length ?? 0) === 1 ? t('dashboard.company') : t('dashboard.companies')}
                    </Tag>
                  </Flex>
                ))}
              </Flex>
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('dashboard.noClientData')} />
            )}
          </Card>
        </Col>
      </Row>

      {/* Registered Modules */}
      {!modulesLoading && availableModules && availableModules.modules.length > 0 && (
        <Card
          title={<IconCardTitle icon={<AppstoreOutlined />} color={token.orange} title={t('dashboard.registeredModules')} />}
          style={{ marginTop: token.marginLG }}
        >
          <Flex gap={token.marginXS} wrap="wrap">
            {availableModules.modules.map((mod) => (
              <Tag key={mod.id} color="orange" style={{ marginInlineEnd: 0, paddingInline: token.paddingXS }}>
                {mod.icon} {mod.name}
                <Text type="secondary" style={{ marginInlineStart: token.marginXXS, fontSize: token.fontSizeSM }}>
                  v{mod.version}
                </Text>
              </Tag>
            ))}
          </Flex>
        </Card>
      )}
    </DashboardPage>
  );
}
